/* eslint-disable no-promise-executor-return */
import JwtDecode from 'jwt-decode';
import * as Storage from '../utils/local-storage';
import {
	CreateSession,
	CheckInvitationForUser,
} from '../connection/app-entry';
import {
	IsProducerForEventsSession,
	LoadMeeting,
	LoadMeetingsEventsLegacyProducer,
	LoadMeetingsStandaloneGreenroomProducer,
} from '../connection/meeting';
import { ShowroomsTokens } from './reducers/session';
import { UserExists } from '../connection/greenroom';
import {
	AddLoginActivity,
	GetGrUserFromAccessToken,
	GetPresenterAllowed,
	isBrandliveEmployee,
} from '../connection/auth';
import { ACTIVITY_TYPES } from '../types/constants';
import { platformAuthUrl } from '../utils/utils';
import { appLogger } from '../utils/datadog';
import blpAuth from '../connection/blp-auth';
import {
	SIGN_IN_HOST,
	SIGN_IN_PRESENTER,
	getProfile,
} from './actions/session';
import {
	ProgramViewType,
	URL_STRINGS,
	LimitedInputConfig,
	LimitedInputViewType,
} from './types/session';
import { VerifyOnsiteLocationForMeeting, VerifyVenueConnection, SetMediaRegionForRoom } from '../connection/video';
import { setOnsiteLocationSession } from './actions/video';
import { OnsiteLocationSessionRoute } from './reducers/video';
import { setModalState } from './actions/modal';
import { MODAL_TEMPLATES } from './reducers/modal';
import { setEnablerShotId } from './actions/meeting';

export function checkSignedIn() {
	try {
		const token = Storage.getStorageItem('token');
		if (token) {
			const user: any = JwtDecode(token);
			if (user.gr_user) {
				return true;
			}
		}

		return false;
	} catch (e) {
		// eslint-disable-next-line no-console
		console.error(e);
		return false;
	}
}

export const APP = {
	SHOWROOMS: 'showrooms',
	EVENTS_LEGACY: 'eventsLegacy',
	EVENTS: 'events',
	ALL_HANDS: 'allhands',
	GREENROOM: 'greenroom',
};

export const ONBOARDING_ROUTES = {
	JOIN_PRESENTER: 0,
	PRESENTER_LOGIN: 1,
	ONBOARDING: 2,
	PRODUCER_LOGIN: 3,
	PRODUCER_SELECT_MEETING: 4,
	PRESENTER_CREATE_ACCOUNT: 5,
	PRODUCER_CONFIRM: 6,
	PRESENTER_SELECT_MEETING: 7,
	PRESENTER_FORGOT_PASSWORD: 8,
	PRESENTER_RESET_PASSWORD: 9,
	PRODUCER_FORGOT_PASSWORD: 10,
	PRODUCER_RESET_PASSWORD: 11,
	PRODUCER_ENTER_EMAIL: 12,
	PRODUCER_CREATE_ACCOUNT: 13,
	PRODUCER_SELECT_APP: 14,
};

export const ONBOARDING_STATES = {
	CONFIRM_INFO: 0,
	DEVICE_SELECT: 1,
	AUDIO_VIDEO_CHECK: 2,
	SPEED_TEST: 3,
};

export interface PageState {
	meeting: any | null;
	session: number | null;
	error: string | null;
	onboarding_route: number;
	invitation?: string | null;
	onboardingComplete?: boolean;
	host?: boolean;
	logout?: boolean;
	skipApproval?: boolean;
	embed?: boolean;
	programView?: ProgramViewType;
	token?: string;
	shouldRefresh?: boolean;
	watcher_only?: boolean;
	user: any | null;
	appSignedInWith: string;
	limitedInput?: boolean;
	limitedInputSmartCanvas?: boolean;
}

interface SessionInvitation {
	[key: string]: number;
}

// FYI - this baseState gets merged into the session
// reducer when resolve() is called in the functions below.
// It all starts with setPageStatus() in App.tsx.
const baseState: PageState = {
	session: null,
	meeting: null,
	error: null,
	onboarding_route: ONBOARDING_ROUTES.JOIN_PRESENTER,
	invitation: null,
	onboardingComplete: false,
	host: false,
	logout: false,
	skipApproval: false,
	embed: false,
	programView: ProgramViewType.None,
	token: '',
	shouldRefresh: false,
	user: null,
	appSignedInWith: '',
	limitedInput: false,
	limitedInputSmartCanvas: false,
};

export const handleLoginActivity = async (token: string, role: string | undefined) => {
	if (
		token
		&& role !== URL_STRINGS.EMBED
		&& role !== URL_STRINGS.STREAM
		&& role !== URL_STRINGS.ONSITE
	) {
		const now = new Date();
		const loginActivity = Storage.getStorageObject('login-activity');

		// If a producer signs in, they can change between producer and
		// presenter views without having to login again.
		// If a user with producer access logs in as a presenter, they will
		// have to login again to access the producer view.
		const producerSignedIn = Storage.getStorageItem('producer-signed-in');

		// New login activity will be inserted in the database when:
		// 1. A producer or presenter signs in ('LOGIN')
		// 2. An already signed-in user opens the app on a day following
		// the date of their last registered login activity ('DAILY_LOGIN')
		let addNewLoginActivity = '';
		if (!loginActivity || (loginActivity && !loginActivity.producer && producerSignedIn)) {
			addNewLoginActivity = ACTIVITY_TYPES.LOGIN;
		} else if (
			loginActivity
			&& loginActivity.nextTime
			&& (now > new Date(loginActivity.nextTime))
		) {
			addNewLoginActivity = ACTIVITY_TYPES.DAILY_LOGIN;
		}

		if (addNewLoginActivity) {
			// Add new login activity to the database and set the
			// local storage object to next insert login activity
			// after midnight (their local time) if they haven't logged out
			const isSuccessful = await AddLoginActivity(
				token,
				addNewLoginActivity,
				window.location.hostname,
				role === URL_STRINGS.PRODUCER ? Storage.getStorageItem('app-signed-in-with') : null,
			);
			if (isSuccessful) {
				now.setHours(24, 0, 0, 0);
				Storage.setStorageObject('login-activity', {
					producer: role === URL_STRINGS.PRODUCER,
					presenter: true,
					nextTime: now.valueOf(),
				});
			}
		}
	}
};

async function handleHost(
	prevSessions: SessionInvitation,
	prevInvitations: SessionInvitation,
	id: string,
	token: string,
	user: any,
	resolve: (state: PageState) => void,
) {
	// let session = prevSessions[id];

	let meeting;
	// Pass showrooms access token to the API if it exists.
	// We determine what app the meeting belongs to on the
	// backend based on which app column ID is filled out in gr_meetings.
	const showroomsTokens = Storage.getStorageItem('showrooms-tokens');
	if (showroomsTokens?.accessToken) {
		meeting = await LoadMeeting(token, id, showroomsTokens?.accessToken);
	} else {
		meeting = await LoadMeeting(token, id);
	}

	if (!meeting) {
		return resolve({
			...baseState,
			onboarding_route: ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING,
			host: true,
		});
	}

	// If producer pasted a URL into browser with a GR meeting_id
	// appended and that meeting is not from the app
	// they signed in with, sign them out. (EX: https://greenroom.brandlive.com/producer/t15bklx). When they sign
	// in they will be redirected back to the GR.
	const appSignedInWith = Storage.getStorageItem('app-signed-in-with');
	switch (appSignedInWith) {
		case APP.EVENTS:
			if (!meeting.events_session_uuid) {
				return resolve({ ...baseState, logout: true });
			}
			break;
		case APP.EVENTS_LEGACY:
			if (!meeting.brandlive_event) {
				return resolve({ ...baseState, logout: true });
			}
			break;
		case APP.GREENROOM:
			if (!meeting?.standalone_title) {
				return resolve({ ...baseState, logout: true });
			}
			break;
		case APP.ALL_HANDS:
			if (!meeting.allhands_id) {
				return resolve({ ...baseState, logout: true });
			}
			break;
		case APP.SHOWROOMS:
			if (!meeting.showrooms_session_id) {
				return resolve({ ...baseState, logout: true });
			}
			break;
		default:
			break;
	}

	return resolve({
		...baseState, meeting, onboarding_route: ONBOARDING_ROUTES.PRODUCER_CONFIRM, host: true,
	});
}

async function handleInvite(
	prevSessions: SessionInvitation,
	prevInvitations: SessionInvitation,
	id: string,
	token: string,
	user: any,
	resolve: (state: PageState) => void,
) {
	const signedIn = !!token && !!user;

	// If verify & email params are in the url, this is a create-account link.
	const url = new URL(window.location.href);
	const code = url.searchParams.get('verify');
	// URLSearchParams interprets + as a space.
	// We use url.href and split it to permit the creation
	// of accounts that use special characters like +.
	const email: string | null = url.href.split('email=')[1] ? url.href.split('email=')[1] : null;

	// The first path segment for an invitation link should be a meeting_id
	const [firstPathSegment] = url.pathname.split('/').filter((item) => !!item);
	const meetingId = firstPathSegment && firstPathSegment !== URL_STRINGS.CREATE
		? firstPathSegment
		: null;

	if (token && email) {
		const user2 = await JwtDecode(token) as any;
		if (user2 && user2.email === email) {
			baseState.user = user2;
			baseState.token = token;
		}
	}

	if (code && email) {
		const result = await UserExists(email);
		if (result.exists) {
			// User was already created manually between the time the invite went out and
			// when the user clicked the invite button in the email.
			// Remove params from url and continue below.
			url.searchParams.delete('verify');
			url.searchParams.delete('email');

			window.history.replaceState(null, '', url.href.replace(`/${URL_STRINGS.CREATE}`, ''));
		} else {
			// Take user to create account page where they will create an account,
			// be auto verified, and taken to the meeting
			return resolve({
				...baseState,
				token: undefined,
				user: undefined,
				onboarding_route: ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT,
			});
		}
	}

	if (!signedIn) {
		return resolve({
			...baseState,
			shouldRefresh: true,
			onboarding_route: ONBOARDING_ROUTES.PRESENTER_LOGIN,
		});
	}

	if (meetingId) {
		const invited = await CheckInvitationForUser(meetingId, token);
		if (invited) {
			const prevApprovedInvite = Storage.getStorageItem(meetingId);

			// generate a session
			const session = await CreateSession(token, meetingId, invited.code, `${user.first_name} ${user.last_name}`);
			const meeting = await LoadMeeting(token, meetingId);

			// set session and invitation in storage
			const newPrevInvs = prevInvitations;
			const newPrevSessions = prevSessions;
			newPrevInvs[id] = meeting.meeting;
			newPrevSessions[id] = session.session;
			baseState.onboarding_route = ONBOARDING_ROUTES.ONBOARDING;

			Storage.setStorageObject('sessions', newPrevSessions);
			Storage.setStorageObject('invitations', newPrevInvs);

			return resolve({
				...baseState,
				session: session.session,
				meeting,
				watcher_only: !!invited.watcher_only,
				skipApproval: !!prevApprovedInvite,
			});
		}
	}
	// if the meeting_id or invitation are invalid,
	// return them to the login route without the meeting_id

	return resolve({ ...baseState, onboarding_route: ONBOARDING_ROUTES.PRESENTER_LOGIN, error: 'BAD_INVITE' });
}

enum AuthResponse {
	NeedsReAuth = 'reauth',
	NoReAuth = 'noreauth',
	NoLoginPath = 'nologinpath',
}

interface AuthResponseStatus {
	authUrl: string | null;
	status: AuthResponse;
}

async function platformAuthResponse(
	token: string,
	meetingHash?: string,
	isPlatform?: boolean,
): Promise<AuthResponseStatus> {
	const response: AuthResponseStatus = {
		authUrl: null,
		status: AuthResponse.NeedsReAuth,
	};

	if (!isPlatform) {
		appLogger.info('platformAuthResponse: Not a Platform room. Allow user to continue.');

		return { ...response, status: AuthResponse.NoReAuth };
	}

	if (!meetingHash) {
		appLogger.info('platformAuthResponse: Missing meetingHash. Should not happen.');

		return { ...response, status: AuthResponse.NoLoginPath };
	}

	const presenterAllowed = await GetPresenterAllowed(token, meetingHash);

	if (!presenterAllowed) {
		appLogger.info('platformAuthResponse: No presenter allowed info. Should not happen.');

		return { ...response, status: AuthResponse.NoLoginPath };
	}

	response.authUrl = presenterAllowed.auth_required;

	if (presenterAllowed.allowed) {
		appLogger.info('platformAuthResponse: User is authorized by Platform to continue.');

		return { ...response, status: AuthResponse.NoReAuth };
	}

	appLogger.warn('platformAuthResponse: User sent for re-auth since not authorized by Platform to continue.');

	return { ...response, status: AuthResponse.NeedsReAuth };
}

async function handleMeetingUrl(
	prevSessions: SessionInvitation,
	prevInvitations: SessionInvitation,
	id: string | undefined,
	token: string,
	user: any,
	resolve: (state: PageState) => void,
	unifiedAuthProps: UnifiedAuthProps,
	limitedInput: boolean,
) {
	const signedIn = !!token && !!user;
	const skipApproval = id ? !!Storage.getStorageItem(id) : false;
	const showroomsTokensJWT = Storage.getStorageItem('showrooms-tokens');
	let showroomsTokens;
	if (showroomsTokensJWT) {
		showroomsTokens = JwtDecode(showroomsTokensJWT) as ShowroomsTokens;
	}

	if (!signedIn) {
		return resolve({
			...baseState,
			shouldRefresh: true,
			onboarding_route: ONBOARDING_ROUTES.PRESENTER_LOGIN,
		});
	}

	if (id) {
		const invited = await CheckInvitationForUser(id, token);

		if (invited) {
			// generate a session
			const session = await CreateSession(token, id, id, `${user.first_name} ${user.last_name}`);
			const meeting = await LoadMeeting(token, id, showroomsTokens?.accessToken);

			appLogger.info(
				`handleMeetingUrl: Starting auth check for user ${user?.gr_user} in meeting ${meeting?.meeting}.`,
			);

			const userNeedsAuth = await platformAuthResponse(
				token,
				meeting?.meeting_id,
				!!meeting?.events_session_uuid,
			);
			const authUrl = userNeedsAuth.authUrl || platformAuthUrl({
				userEmail: user?.email,
				channelId: meeting?.channel,
			});

			appLogger.info(`handleMeetingUrl: userNeedsAuth: ${userNeedsAuth}`);

			if (userNeedsAuth.status === AuthResponse.NoLoginPath) {
				appLogger.info(`handleMeetingUrl: Denying access to meeting ${meeting?.meeting}.`);

				window.history.pushState(null, 'Greenroom', '/?accessDenied=true');

				return resolve({
					...baseState,
					onboarding_route: ONBOARDING_ROUTES.PRESENTER_LOGIN,
					error: 'BAD_INVITE',
				});
			}

			if (userNeedsAuth.status === AuthResponse.NeedsReAuth && !unifiedAuthProps.token) {
				appLogger.info('handleMeetingUrl: Sending user to re-auth.');

				window.location.href = authUrl;
			} else if (unifiedAuthProps.token) {
				unifiedAuthProps.finishPresenter();
			}

			// set session and invitation in storage
			// eslint-disable-next-line no-param-reassign
			prevInvitations[id] = meeting.meeting;
			// eslint-disable-next-line no-param-reassign
			prevSessions[id] = session.session;
			baseState.onboarding_route = ONBOARDING_ROUTES.ONBOARDING;

			Storage.setStorageObject('sessions', prevSessions);
			Storage.setStorageObject('invitations', prevInvitations);

			return resolve({
				...baseState,
				session: session.session,
				meeting,
				watcher_only: !!invited.watcher_only,
				skipApproval,
				limitedInput,
			});
		}
	}

	return resolve({ ...baseState, onboarding_route: ONBOARDING_ROUTES.PRESENTER_LOGIN, error: 'BAD_INVITE' });
}

async function handleEmbed(
	id: string | undefined,
	token: string,
	role: string,
	resolve: (state: PageState) => void,
) {
	const meeting = id ? await LoadMeeting(token, id) : null;

	let programView = ProgramViewType.None;
	if (role === URL_STRINGS.STREAM) {
		programView = ProgramViewType.ProgramFeed;
	} else if (role === URL_STRINGS.ONSITE) {
		programView = ProgramViewType.OnsiteFeed;
	}

	return resolve({
		...baseState,
		token,
		embed: true,
		programView,
		meeting,
	});
}

async function handleOnsiteLocation(
	token: string,
	meetingSlug: string,
	onsiteLocationId: number | null,
	isCanvasView: boolean,
	resolve: (state: PageState) => void,
	limitedInput: boolean = false,
	limitedInputSmartCanvas: boolean = false,
) {
	const store = (await import('./main')).default;

	let route = OnsiteLocationSessionRoute.Entry;

	if (isCanvasView) {
		if (limitedInput) {
			route = OnsiteLocationSessionRoute.LimitedInputCanvas;
			// if limited input, remove all query params from the window location
			const url = new URL(window.location.href);
			url.searchParams.delete('limited-input');
			url.searchParams.delete('view');
			url.searchParams.delete('smartCanvas');
			url.searchParams.delete('locationId');
			window.history.replaceState({}, '', url.href);
		} else {
			route = OnsiteLocationSessionRoute.Canvas;
			store.dispatch(setModalState({
				modalOpen: true,
				modalTemplate: MODAL_TEMPLATES.ONSITE_LOCATION,
			}));
		}
	}

	store.dispatch(setOnsiteLocationSession(
		{
			id: onsiteLocationId,
			route,
		},
	));

	const meeting = await LoadMeeting(token, meetingSlug);

	return resolve({
		...baseState,
		onboardingComplete: route !== OnsiteLocationSessionRoute.Canvas,
		token,
		embed: isCanvasView,
		programView: ProgramViewType.OnsiteLocation,
		meeting,
		limitedInput,
		limitedInputSmartCanvas,
	});
}

async function allowProgramOrOnsiteFeed(meetingId: string, userToken: string): Promise<boolean> {
	// User's that have permission to open program and onsite program feeds:
	// - Invited presenters
	// - Brandlive employees (for virtual tech/debugging purposes)
	// - Producers that have access to the channel that the GR is on

	try {
		// Was user invited to this GR as a presenter
		if (await CheckInvitationForUser(meetingId, userToken)) {
			return true;
		}
		if (await isBrandliveEmployee(userToken)) {
			return true;
		}
		// Is user a producer with access to the channel the GR is on?
		const appSignedInWithStorage = Storage.getStorageItem('app-signed-in-with');
		switch (appSignedInWithStorage) {
			case APP.ALL_HANDS:
				// TO DO allhands
				return false;
			case APP.EVENTS:
				// For Events we only load GRs for the channel you are on.
				// So we need to take the meetingId from the URL
				// and run an API call to verify if you are a producer for the GR.
				// eslint-disable-next-line no-case-declarations
				const isProducerForMeeting = await IsProducerForEventsSession(userToken, meetingId);
				return isProducerForMeeting;
			case APP.EVENTS_LEGACY:
				if (
					((await LoadMeetingsEventsLegacyProducer(userToken)) as any[]).some(
						(event) => event.meeting_id === meetingId,
					)
				) {
					return true;
				}
				return false;

			case APP.SHOWROOMS:
				// TO DO showrooms
				return false;
			case APP.GREENROOM:
				if (
					(
						(await LoadMeetingsStandaloneGreenroomProducer(userToken)) as any[]
					).some((event) => event.meeting_id === meetingId)
				) {
					return true;
				}
				return false;

			default:
				return false;
		}
	} catch {
		return false;
	}
}

type UnifiedAuthProps = {
	token: string | undefined,
	user: any | undefined,
	host: boolean,
	finishPresenter: () => void,
	finishProducer: () => void,
}

const handleUnifiedAuth = async (): Promise<UnifiedAuthProps> => {
	try {
		const store = (await import('./main')).default;
		await blpAuth.handleRedirected();
		if (blpAuth.refreshToken) {
			// use refresh token to get new access token on each visit
			await blpAuth.refresh();
			blpAuth.initializeActivityTracker({ refreshOnInterval: true });

			// get gr_user from access token
			const profile = await GetGrUserFromAccessToken();

			// if there is a token we should return the data as well as the redux actions
			// that should be fired when all other required checks are completed
			if (profile) {
				return {
					token: profile.token,
					user: JwtDecode(profile.token),
					host: !!profile.isProducer,
					// user could be presenter or producer depending on context and token value
					finishPresenter: () => {
						store.dispatch({
							type: SIGN_IN_PRESENTER,
							promise: new Promise((resolve) => {
								resolve({
									token: profile.token,
									app_signed_in_with: 'events',
								});
							}),
						});
					},
					finishProducer: () => {
						if (profile.isProducer) {
							store.dispatch({
								type: SIGN_IN_HOST,
								promise: new Promise((resolve) => {
									resolve({
										token: profile.token,
										events_legacy_channels: [],
										events_channels: [],
										gr_channels: [],
										selectedApp: 'events',
									});
								}),
							});
							store.dispatch(getProfile(profile.token, 'events'));
						}
					},
				};
			}
		} else {
			// refresh token is not available, so we should sign the user out
			// to clear out cookies and local storage.
			await blpAuth.signOut();
			// user does not have a refresh token, they should be redirected to the auth app
			blpAuth.redirectToSignIn({ redirect_uri: window.location.href });
		}

		return {
			token: undefined,
			user: undefined,
			host: false,
			finishPresenter: () => { },
			finishProducer: () => { },
		};
	} catch (e) {
		console.error(e);
		blpAuth.signOut()
			.then(() => {
				// If there was an error, we should sign the user out
				// and redirect them to the login page
				blpAuth.redirectToSignIn({ redirect_uri: window.location.href });
			})
		return {
			token: undefined,
			user: undefined,
			host: false,
			finishPresenter: () => { },
			finishProducer: () => { },
		};
	}
};

export const roleAndMeetingSlug = (): {role?: string, id?: string} => {
	const path = window.location.pathname;
	// role is the first component of the url, id is the second component of the url
	// if the user visits /producer/12345, the role is 'host', the meeting id is 12345
	// if the user visits /invite/12345, the role is 'invites', the
	// invite id is 12345 (not the meeting id, which will be found after the invite is checked)
	// if the user visits /12345, the role is 12345, and is thus the meeting id.
	// if the user visits /, then there's no role or meeting, and they'll be presented
	// with the meeting id form
	const [role, id] = path.split('/').filter((item) => !!item);
	return { role, id };
};

/**
 * This is kind of a lot, but here's the gist:
 *
 * We need to make a determination as to what state we
 * should present the user with depending on a few factors:
 *
 * 1. Are they signed in?
 * 2. Are they a host?
 * 3. Do they have a previous session stored?
 * 4. Is their session id valid?
 * 5. Are they attempting to accept an invitation?
 * 6. Is their invitation valid?
 * 7. Are they attempting to go directly to a meeting? If so, do we have stored a valid invitation?
 */
export default function checkStatus(): Promise<PageState> {
	// eslint-disable-next-line no-async-promise-executor, consistent-return
	return new Promise(async (resolve, reject) => {
		try {
			// eslint-disable-next-line global-require
			const store = require('./main').default;
			let { role, id } = roleAndMeetingSlug();

			const isEmbed = role === URL_STRINGS.EMBED;
			if (isEmbed) {
				const enablerShotId = new URLSearchParams(location.search).get('enabler-shot');
				if (enablerShotId) {
					store.dispatch(setEnablerShotId(Number(enablerShotId)));
				}
			}

			const limitedInput = window.location.search.includes('limited-input=1');
			// set the limited input config from the redirect query params
			let limitedInputConfig: LimitedInputConfig | null = null;
			if (limitedInput) {
				const url = new URL(window.location.href);
				const view = url.searchParams.get('view') ?? 'canvas';
				const smartCanvas = url.searchParams.get('smartCanvas') === 'true' || false;
				const locationId = Number(url.searchParams.get('locationId')) || null;
				limitedInputConfig = {
					view: view as LimitedInputViewType,
					smartCanvas,
					locationId,
				};
			}

			let unifiedAuthProps: UnifiedAuthProps = {
				token: undefined,
				user: undefined,
				host: false,
				finishPresenter: () => { },
				finishProducer: () => { },
			};

			let unifiedAuthUsed = false;

			if (!isEmbed) {
				unifiedAuthProps = await handleUnifiedAuth();

				if (unifiedAuthProps.token) {
					unifiedAuthUsed = true;
					baseState.token = unifiedAuthProps.token;
					baseState.user = unifiedAuthProps.user;
					baseState.appSignedInWith = APP.EVENTS;
					Storage.setStorageItem('app-signed-in-with', APP.EVENTS);
				}
			}

			let token = unifiedAuthProps.token ?? Storage.getStorageItem('token');

			handleLoginActivity(token, role);

			let user: any = unifiedAuthProps.user ?? {};

			const producerSignedIn = unifiedAuthProps.host && unifiedAuthProps.token;

			// If the user opened the app from an email link to "create account" or "reset password"
			// and someone was already logged in, don't load the signed in user.
			if (token
				&& !window.location.href.includes(URL_STRINGS.CREATE)
				&& !window.location.href.includes(URL_STRINGS.PASSWORD)
				&& !unifiedAuthUsed
			) {
				user = JwtDecode(token);

				/*
				Cover a corner case where a user that logged in before decoupling was
				merged in might not have the `gr_user` property in the local storage
				token that is key to certain app functions like dragging and dropping
				shots into the canvas.
				*/
				if (!user.gr_user) {
					resolve({ ...baseState, logout: true });
				}

				baseState.token = token;
				baseState.user = user;
			}

			if (!unifiedAuthUsed) {
				const appSignedInWithStorage = Storage.getStorageItem('app-signed-in-with');
				if (appSignedInWithStorage) baseState.appSignedInWith = appSignedInWithStorage;
			}

			const url = new URL(window.location.href);
			const code = url.searchParams.get('verify');
			if (role === URL_STRINGS.VERIFY) {
				if (code) {
					return resolve({
						...baseState,
						onboarding_route: ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING,
					});
				}
				// verify-account path is not valid without a code
				// remove it so it is not mistaken for a meeting id
				window.history.replaceState(null, '', url.href.replace(`/${URL_STRINGS.VERIFY}`, ''));
				role = '';
			}

			// open onsite location canvas view if limited input config view is canvas
			if (limitedInputConfig && limitedInputConfig.view === 'canvas' && token && role) {
				// capture the conference room device's IP for media server region detection
				const venueResult = await VerifyVenueConnection(token, role);
				console.log("🚀 ~ checkStatus ~ venueResult:", venueResult)
				if (venueResult?.region && venueResult.meeting) {
					await SetMediaRegionForRoom(token, venueResult.meeting, venueResult.region);
				}
				return handleOnsiteLocation(
					token,
					role, // role is the meeting slug for presenters
					limitedInputConfig.locationId,
					true,
					resolve,
					limitedInput,
					limitedInputConfig.smartCanvas,
				);
			}

			// user has gone to greenroom.brandlive.com, they are not signed in and have entered nothing
			if (!role) {
				if (user && user.gr_user) {
					unifiedAuthProps.finishPresenter();
					resolve({
						...baseState,
						onboarding_route: ONBOARDING_ROUTES.PRESENTER_SELECT_MEETING,
					});
				}
				resolve({ ...baseState, onboarding_route: ONBOARDING_ROUTES.PRESENTER_LOGIN });
			}

			if (role === URL_STRINGS.CREATE && !unifiedAuthUsed) {
				resolve({ ...baseState, onboarding_route: ONBOARDING_ROUTES.PRESENTER_CREATE_ACCOUNT });
			}

			let prevSessions = Storage.getStorageObject('sessions');
			let prevInvitations = Storage.getStorageObject('invitations');
			if (!prevSessions) prevSessions = {};
			if (!prevInvitations) prevInvitations = {};

			// If the role is not 'producer' and the id is 'create-account',
			// this is a user creating a new account from an invitation
			if (role !== URL_STRINGS.PRODUCER && id === URL_STRINGS.CREATE) {
				unifiedAuthProps.finishPresenter();
				return handleInvite(prevSessions, prevInvitations, id, token, user, resolve);
			}

			if (role === URL_STRINGS.PASSWORD && !unifiedAuthUsed) {
				return resolve({
					...baseState,
					onboarding_route: ONBOARDING_ROUTES.PRESENTER_RESET_PASSWORD,
				});
			}

			if (
				role === URL_STRINGS.EMBED
				|| (
					id
					&& token
					&& (role === URL_STRINGS.STREAM || role === URL_STRINGS.ONSITE)
					&& (await allowProgramOrOnsiteFeed(id, token))
				)
			) {
				token = role === URL_STRINGS.EMBED ? process.env.REACT_APP_SYNC_AUTH : token;
				return handleEmbed(id, token, role, resolve);
			}

			// role is an onsite location
			if (role && /onsite\d+/.test(role) && token && id) {
				const verifyResult = await VerifyOnsiteLocationForMeeting(token, id, role);
				console.log("🚀 ~ checkStatus ~ onsite verifyResult:", verifyResult)

				// accessible if onsite location exists and the user is a producer
				if (verifyResult?.id && producerSignedIn) {
					// if the verify endpoint resolved a media region from the venue's IP,
					// trigger session recreation with the optimal region hint
					if (verifyResult.region && verifyResult.meeting) {
						SetMediaRegionForRoom(token, verifyResult.meeting, verifyResult.region);
					}

					return handleOnsiteLocation(
						token,
						id,
						verifyResult.id,
						url.searchParams.get('view') === 'canvas',
						resolve,
						false,
						false,
					);
				}
			}

			// user has gone to greenroom.brandlive.com/producer
			if (role === URL_STRINGS.PRODUCER) {
				if (id === URL_STRINGS.CREATE && !unifiedAuthUsed) {
					return resolve({
						...baseState,
						host: true,
						onboarding_route: ONBOARDING_ROUTES.PRODUCER_CREATE_ACCOUNT,
					});
				}

				if (id === URL_STRINGS.PASSWORD && !unifiedAuthUsed) {
					return resolve({
						...baseState,
						onboarding_route: ONBOARDING_ROUTES.PRODUCER_RESET_PASSWORD,
					});
				}

				if (id === URL_STRINGS.VERIFY && !unifiedAuthUsed) {
					if (code) {
						return resolve({
							...baseState,
							host: true,
							onboarding_route: ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING,
						});
					}
					// verify-account path is not valid without a code
					// remove it so it is not mistaken for a meeting id
					window.history.replaceState(null, '', url.href.replace(`/${URL_STRINGS.VERIFY}`, ''));
					id = '';
				}

				// Need to check that this user has successfully signed in as producer in the past.
				if (user
					&& (
						(user.gr_producer && user.verified)
						|| user.events_producer
						|| user.events_legacy_producer
						|| user.showrooms_producer
					)
					&& producerSignedIn) {
					unifiedAuthProps.finishProducer();
					if (id) {
						return handleHost(prevSessions, prevInvitations, id, token, user, resolve);
					}
					resolve({
						...baseState,
						host: true,
						onboarding_route: ONBOARDING_ROUTES.PRODUCER_SELECT_MEETING,
					});
				} else {
					// window.history.pushState(null, 'Greenroom', '/producer');
					// Let the handleMeetingUrl decide
					return handleMeetingUrl(
						prevSessions,
						prevInvitations,
						role,
						token,
						user,
						resolve,
						unifiedAuthProps,
						limitedInput,
					);
				}
			} else {
				// if just a string was passed that's neither host nor invite, role is the id
				return handleMeetingUrl(
					prevSessions,
					prevInvitations,
					role,
					token,
					user,
					resolve,
					unifiedAuthProps,
					limitedInput,
				);
			}
		} catch (e) {
			reject(e);
		}
	});
}
